import { Component, Input } from '@angular/core';

/** Which concept the animated illustration explains. */
export type DocHint =
  | 'frente'
  | 'verso'
  | 'qr'
  | 'cnh-abrir'
  | 'verso-amostra'
  /** The folded document's OUTER back — the vehicle-categories side (what Veriff verifies). */
  | 'cnh-verso'
  /** The folded document's INNER page — the QR/MRZ surface, reached by opening it. */
  | 'cnh-interna';

/** One filled module (black square) in the QR illustration. */
interface QrModule {
  x: number;
  y: number;
}

/**
 * Small animated, asset-free illustration that explains a capture concept to
 * low-literacy users: what the **frente** (front — the side with the photo) and
 * **verso** (back) of a document are, and what a **QR** code looks like. Drawn
 * with inline SVG + CSS keyframes (no real-document images, so no PII), with a
 * short caption underneath.
 */
@Component({
  selector: 'app-doc-illustration',
  templateUrl: './doc-illustration.html',
  styleUrls: ['./doc-illustration.scss'],
})
export class DocIllustration {
  @Input() kind!: DocHint;

  /** Module count per side — large/dense, like the real CNH-e / CIN codes. */
  protected readonly qrN = 33;
  /** The filled modules of a realistic dense QR (finder + timing + data). */
  protected readonly qrModules: QrModule[] = buildQrModules(this.qrN);
}

/**
 * Build a realistic-looking (not decodable) QR module map: three corner finder
 * patterns with separators, the timing rows, an alignment pattern, and a dense
 * deterministic "data" fill — so it reads as the large, busy code users actually
 * see on their documents rather than a sparse grid.
 */
function buildQrModules(n: number): QrModule[] {
  const on: boolean[][] = Array.from({ length: n }, () => Array<boolean>(n).fill(false));
  const reserved: boolean[][] = Array.from({ length: n }, () => Array<boolean>(n).fill(false));

  // 7×7 finder "bullseye" plus its 1-module light separator.
  const finder = (r0: number, c0: number) => {
    for (let r = -1; r <= 7; r++) {
      for (let c = -1; c <= 7; c++) {
        const rr = r0 + r;
        const cc = c0 + c;
        if (rr < 0 || cc < 0 || rr >= n || cc >= n) continue;
        reserved[rr][cc] = true;
        const inRing = r >= 0 && r <= 6 && c >= 0 && c <= 6;
        const edge = r === 0 || r === 6 || c === 0 || c === 6;
        const core = r >= 2 && r <= 4 && c >= 2 && c <= 4;
        on[rr][cc] = inRing && (edge || core);
      }
    }
  };
  finder(0, 0);
  finder(0, n - 7);
  finder(n - 7, 0);

  // Timing patterns: alternating modules along row 6 and column 6.
  for (let i = 0; i < n; i++) {
    if (!reserved[6][i]) {
      on[6][i] = i % 2 === 0;
      reserved[6][i] = true;
    }
    if (!reserved[i][6]) {
      on[i][6] = i % 2 === 0;
      reserved[i][6] = true;
    }
  }

  // A 5×5 alignment pattern toward the bottom-right.
  const a = n - 9;
  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 5; c++) {
      const rr = a + r;
      const cc = a + c;
      reserved[rr][cc] = true;
      const edge = r === 0 || r === 4 || c === 0 || c === 4;
      on[rr][cc] = edge || (r === 2 && c === 2);
    }
  }

  // Deterministic, hash-based fill for the data area (~48% dark, like a real QR).
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (reserved[r][c]) continue;
      let h = (r * 73856093) ^ (c * 19349663) ^ ((r + 1) * (c + 1) * 83492791);
      h = (h ^ (h >>> 13)) >>> 0;
      on[r][c] = h % 100 < 48;
    }
  }

  const modules: QrModule[] = [];
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (on[r][c]) modules.push({ x: c, y: r });
    }
  }
  return modules;
}
